/**
 * The `/discount` evaluation surface — the monolith's promo endpoints.
 *
 * These four routes (`eligible`, `candidates`, `validate`, `calculate`) were bound
 * to a local `interface Body_` inside `discount-compat.controller.ts`. That is a
 * Rule #2 violation on its own — an HTTP request shape declared in a service
 * rather than here — and it had a runtime cost: an interface has no metatype at
 * runtime, so the global ValidationPipe skipped the body and
 * `body?.discountId ?? ''` sent an empty string into Prisma, which answered 500.
 *
 * `Cart` and `EvalContext` still live in `discount-compat/discount-conditions.ts`
 * because they are also the evaluator's internal working types. These schemas
 * describe only what a CLIENT may send, which is deliberately narrower — see the
 * note on `userId` below.
 */

import { z } from 'zod';

export const DiscountCartItemSchema = z.object({
  productId: z.string().optional(),
  category: z.string().optional(),
  quantity: z.number().int().min(0).optional(),
  price: z.number().min(0).optional(),
});
export type DiscountCartItem = z.infer<typeof DiscountCartItemSchema>;

export const DiscountCartSchema = z.object({
  items: z.array(DiscountCartItemSchema).optional(),
  totalAmount: z.number().min(0).optional(),
  eventContext: z.object({ eventIds: z.array(z.string()).optional() }).nullable().optional(),
});
export type DiscountCart = z.infer<typeof DiscountCartSchema>;

/**
 * The client-supplied half of the evaluation context.
 *
 * `userId` is deliberately ABSENT and must stay absent. The controller derives it
 * from the token and overwrites whatever arrived, because it drives the
 * per-customer usage cap and the `userIds` allow-list — a body-supplied one would
 * let a caller claim someone else's remaining redemptions. Adding `userId` here
 * would not by itself break that (the controller still overwrites), but it would
 * advertise a field the server ignores, which is how such a guard gets removed
 * later by someone tidying up "unused" code.
 *
 * `usage` is likewise omitted: it is a count the evaluator reads from the
 * database, not an input.
 */
export const DiscountEvalContextSchema = z.object({
  memberId: z.string().nullable().optional(),
  member: z.object({
    memberType: z.string().optional(),
    loyaltyTier: z.string().optional(),
  }).nullable().optional(),
  posType: z.string().nullable().optional(),
  sourceChannel: z.string().nullable().optional(),
  groupType: z.string().nullable().optional(),
  paymentGateway: z.string().nullable().optional(),
  paymentMethodId: z.string().nullable().optional(),
  identity: z.object({
    idCardRegion: z.string().optional(),
    birthMonth: z.number().int().min(1).max(12).nullable().optional(),
    ktpVerified: z.boolean().optional(),
  }).nullable().optional(),
  document: z.object({
    documentType: z.string().optional(),
    fields: z.record(z.string()).optional(),
  }).nullable().optional(),
  isFirstTimeCustomer: z.boolean().optional(),
  visitDate: z.union([z.string(), z.date()]).nullable().optional(),
});
export type DiscountEvalContextInput = z.infer<typeof DiscountEvalContextSchema>;

/** `eligible` and `candidates`: evaluate every discount against a cart. */
export const DiscountEvaluateRequestSchema = z.object({
  cart: DiscountCartSchema.optional(),
  context: DiscountEvalContextSchema.optional(),
});
export type DiscountEvaluateRequest = z.infer<typeof DiscountEvaluateRequestSchema>;

/**
 * `validate` and `calculate`: evaluate ONE named discount.
 *
 * `discountId` is required here and optional nowhere. The handlers previously
 * defaulted it to `''`, which looked up a discount whose id is the empty string —
 * not a miss the service handles, but a Prisma error, hence the 500.
 */
export const DiscountValidateRequestSchema = DiscountEvaluateRequestSchema.extend({
  discountId: z.string().min(1),
});
export type DiscountValidateRequest = z.infer<typeof DiscountValidateRequestSchema>;
